/**
 * Routine Setup Checklist (Getting Started Checklist) state.
 * Separate from profile onboarding; guides users through setting up routines.
 * Persistence: routine_setup_checklist_completed and visited AM/PM in localStorage.
 * Keys are scoped by user id when authenticated so new accounts always see the checklist.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import { guestDataService } from '../services/guestDataService';

const STORAGE_KEYS = {
  COMPLETED: 'dew_routine_setup_checklist_completed',
  DISMISSED: 'dew_routine_setup_checklist_dismissed',
  VISITED_AM: 'dew_routine_setup_visited_am',
  VISITED_PM: 'dew_routine_setup_visited_pm',
};

function getStorageKey(key, userId) {
  return userId != null ? `${key}_${userId}` : key;
}

function getStoredCompleted(userId) {
  try {
    return localStorage.getItem(getStorageKey(STORAGE_KEYS.COMPLETED, userId)) === 'true';
  } catch {
    return false;
  }
}

function getStoredDismissed(userId) {
  try {
    return localStorage.getItem(getStorageKey(STORAGE_KEYS.DISMISSED, userId)) === 'true';
  } catch {
    return false;
  }
}

function setStoredDismissed(value, userId) {
  try {
    const key = getStorageKey(STORAGE_KEYS.DISMISSED, userId);
    if (value) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch (_) {}
}

function getStoredVisitedAM(userId) {
  try {
    return localStorage.getItem(getStorageKey(STORAGE_KEYS.VISITED_AM, userId)) === 'true';
  } catch {
    return false;
  }
}

function getStoredVisitedPM(userId) {
  try {
    return localStorage.getItem(getStorageKey(STORAGE_KEYS.VISITED_PM, userId)) === 'true';
  } catch {
    return false;
  }
}

function setStoredCompleted(value, userId) {
  try {
    const key = getStorageKey(STORAGE_KEYS.COMPLETED, userId);
    if (value) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch (_) {}
}

function setStoredVisitedAM(value, userId) {
  try {
    const key = getStorageKey(STORAGE_KEYS.VISITED_AM, userId);
    if (value) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch (_) {}
}

function setStoredVisitedPM(value, userId) {
  try {
    const key = getStorageKey(STORAGE_KEYS.VISITED_PM, userId);
    if (value) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch (_) {}
}

const defaultProgress = {
  hasCabinetProduct: false,
  hasSuccessfullyUpdatedRoutines: false,
  hasVisitedAMRoutine: false,
  hasVisitedPMRoutine: false,
};

const RoutineSetupChecklistContext = createContext();

export const useRoutineSetupChecklist = () => {
  const ctx = useContext(RoutineSetupChecklistContext);
  if (!ctx) throw new Error('useRoutineSetupChecklist must be used within RoutineSetupChecklistProvider');
  return ctx;
};

export const RoutineSetupChecklistProvider = ({ children }) => {
  const { user, authStatus } = useAuth();
  const [progress, setProgress] = useState(defaultProgress);
  const [routineSetupChecklistCompleted, setRoutineSetupChecklistCompleted] = useState(false);
  const [routineSetupChecklistDismissed, setRoutineSetupChecklistDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightUpdateRoutinesButton, setHighlightUpdateRoutinesButton] = useState(false);

  const userId = user?.id ?? undefined;

  // When user changes (e.g. guest → logged in), re-read checklist state from user-scoped storage
  useEffect(() => {
    setRoutineSetupChecklistCompleted(getStoredCompleted(userId));
    setRoutineSetupChecklistDismissed(getStoredDismissed(userId));
  }, [userId]);

  const fetchProgress = useCallback(async () => {
    if (!user?.id) {
      // Guest: derive from guestDataService
      try {
        const [products, lastUpdate] = await Promise.all([
          guestDataService.getProducts(user),
          guestDataService.getLastRoutineUpdate(user),
        ]);
        const cabinetCount = Array.isArray(products) ? products.length : 0;
        const hasSuccessfullyUpdatedRoutines = lastUpdate != null && lastUpdate !== '';
        setProgress((prev) => ({
          ...prev,
          hasCabinetProduct: cabinetCount >= 1,
          hasSuccessfullyUpdatedRoutines,
          hasVisitedAMRoutine: getStoredVisitedAM(userId),
          hasVisitedPMRoutine: getStoredVisitedPM(userId),
        }));
      } catch (_) {
        setProgress((prev) => ({
          ...prev,
          hasVisitedAMRoutine: getStoredVisitedAM(userId),
          hasVisitedPMRoutine: getStoredVisitedPM(userId),
        }));
      }
      return;
    }
    setLoading(true);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/user/routine-setup-progress`);
      if (res.ok) {
        const data = await res.json();
        const hasCabinetProduct = (data.cabinetCount ?? 0) >= 1;
        const hasSuccessfullyUpdatedRoutines = data.lastRoutineUpdatedAt != null;
        setProgress((prev) => ({
          ...prev,
          hasCabinetProduct,
          hasSuccessfullyUpdatedRoutines,
          hasVisitedAMRoutine: getStoredVisitedAM(userId),
          hasVisitedPMRoutine: getStoredVisitedPM(userId),
        }));
      }
    } catch (_) {
      setProgress((prev) => ({ ...prev }));
    } finally {
      setLoading(false);
    }
  }, [user?.id, userId]);

  useEffect(() => {
    if (authStatus === 'authenticated' && user?.id) {
      fetchProgress();
    } else if (user !== undefined) {
      // Guest or loading
      fetchProgress();
    }
  }, [authStatus, user?.id, fetchProgress]);

  useEffect(() => {
    const { hasCabinetProduct, hasSuccessfullyUpdatedRoutines, hasVisitedAMRoutine, hasVisitedPMRoutine } = progress;
    const allDone = hasCabinetProduct && hasSuccessfullyUpdatedRoutines && hasVisitedAMRoutine && hasVisitedPMRoutine;
    if (allDone && !getStoredCompleted(userId)) {
      setStoredCompleted(true, userId);
      setRoutineSetupChecklistCompleted(true);
    }
  }, [progress, userId]);

  const markUpdateRoutinesSuccess = useCallback(() => {
    setProgress((prev) => ({ ...prev, hasSuccessfullyUpdatedRoutines: true }));
    setHighlightUpdateRoutinesButton(false);
    fetchProgress();
  }, [fetchProgress]);

  const markVisitedAMRoutine = useCallback(() => {
    setStoredVisitedAM(true, userId);
    setProgress((prev) => ({ ...prev, hasVisitedAMRoutine: true }));
  }, [userId]);

  const markVisitedPMRoutine = useCallback(() => {
    setStoredVisitedPM(true, userId);
    setProgress((prev) => ({ ...prev, hasVisitedPMRoutine: true }));
  }, [userId]);

  const triggerHighlightUpdateRoutinesButton = useCallback(() => {
    setHighlightUpdateRoutinesButton(true);
    setTimeout(() => setHighlightUpdateRoutinesButton(false), 1500);
  }, []);

  const refreshProgress = useCallback(() => {
    fetchProgress();
  }, [fetchProgress]);

  const dismissChecklist = useCallback(() => {
    setStoredDismissed(true, userId);
    setRoutineSetupChecklistDismissed(true);
  }, [userId]);

  const value = {
    progress,
    routineSetupChecklistCompleted: routineSetupChecklistCompleted || getStoredCompleted(userId),
    routineSetupChecklistDismissed: routineSetupChecklistDismissed || getStoredDismissed(userId),
    loading,
    refreshProgress,
    dismissChecklist,
    markUpdateRoutinesSuccess,
    markVisitedAMRoutine,
    markVisitedPMRoutine,
    highlightUpdateRoutinesButton,
    triggerHighlightUpdateRoutinesButton,
  };

  return (
    <RoutineSetupChecklistContext.Provider value={value}>
      {children}
    </RoutineSetupChecklistContext.Provider>
  );
};
